import {
  cartDoneMutation,
  updateCartPeriodMutation,
} from '@/api/carts/cartMutations';
import { cartQuery } from '@/api/carts/cartQueries';
import { getOrderQuery, getUserOrdersQuery } from '@/api/orders/orderQueries';
import { createTempOrderMutation } from '@/api/temp-orders/tempOrderMutations';
import { Button } from '@/components/common/Button';
import { Heading } from '@/components/common/Heading';
import { Select } from '@/components/common/Select';
import { Spinner } from '@/components/common/Spinner';
import { UserDataRow } from '@/components/dashboard/account/UserDataRow';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/dashboard/DashboardCard';
import { CartAddItemView } from '@/components/dashboard/my-carts/CartAddItemView';
import { CartItemsList } from '@/components/dashboard/my-carts/CartItemsList';
import { CartPreviewDialog } from '@/components/dashboard/my-carts/CartPreviewDialog';
import { CartSummary } from '@/components/dashboard/my-carts/CartSummarry';
import { MyCartsWrapper } from '@/components/dashboard/my-carts/MyCartsWrapper';
import { OrderCard } from '@/components/dashboard/my-orders/OrderCard';
import { CartPeriod } from '@/domain/Cart';
import type { User } from '@/domain/User';
import { routes } from '@/routes/routes';
import { Calendar as CalendarIcon } from '@carbon/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const SubscriptionPage = () => {
  const params = useParams({ strict: false });
  const orderId = params.orderId ? Number(params.orderId) : undefined;
  const cartIdParam = params.cartId ? Number(params.cartId) : undefined;
  const search = useSearch({ strict: false });
  const isWalkIn = search.walkin === true;

  const navigate = useNavigate();
  const [isAddSupplementOpen, setIsAddSupplementOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isChooseSubscriptionPeriodOpen, setIsChooseSubscriptionPeriodOpen] =
    useState(false);

  const { data: orderResponse, isLoading: isOrderLoading } = useQuery({
    ...getOrderQuery(orderId ?? 0),
    enabled: !!orderId,
  });

  const {
    data: cartResponse,
    isLoading: isCartLoading,
    isFetching,
  } = useQuery({
    ...cartQuery(cartIdParam ?? 0),
    enabled: !!cartIdParam,
  });

  const order = orderResponse?.data;
  const cart = cartResponse?.data;

  const currentCartId = order?.cart_id ?? cart?.id;
  const userId = order?.user_id ?? cart?.user_id;

  const user = (order?.user ?? cart?.user) as
    | (User & {
        clinic?: { id: number; name: string };
      })
    | null;

  const isLoading = isOrderLoading || isCartLoading;

  const { data: userOrdersResponse } = useQuery({
    ...getUserOrdersQuery(userId ?? 0),
    enabled: !!userId,
  });

  const userOrders = userOrdersResponse?.data ?? [];

  const cartPeriodOptions = Object.values(CartPeriod)
    .filter((value) => typeof value === 'number')
    .map((value) => ({
      label: `${value} ${value === CartPeriod.MONTHLY ? 'month' : 'months'}`,
      value: value.toString(),
    }));

  const doneMutation = useMutation({
    ...cartDoneMutation(currentCartId ?? 0),
    onSuccess: () => {
      navigate({ to: routes.dashboard.users });
    },
  });

  const updatePeriodMutation = useMutation({
    ...updateCartPeriodMutation(currentCartId ?? 0),
    onError: () => {
      setIsChooseSubscriptionPeriodOpen(true);
    },
  });

  const createTempOrder = useMutation(createTempOrderMutation());

  // Walk-in flow: turn the finished cart into a Paid order, then jump to the
  // orders list where it shows under PAID.
  const handleCreatePaidOrder = () => {
    if (!currentCartId) return;
    if ((cart?.items?.length ?? 0) === 0) {
      toast.error('Please add at least one supplement to continue');
      return;
    }
    createTempOrder.mutate(
      { cart_id: currentCartId },
      { onSuccess: () => navigate({ to: routes.dashboard.orders }) },
    );
  };

  const handleChangeSubscriptionPeriod = (value: string) => {
    const period = Number(value) as CartPeriod;
    const currentPeriod = order?.period ?? cart?.period;

    if (period === currentPeriod) {
      setIsChooseSubscriptionPeriodOpen(false);
      return;
    }

    updatePeriodMutation.mutate({ period });
    setIsChooseSubscriptionPeriodOpen(false);
  };

  const handleDoneAddingSupplements = () => {
    const itemsCount = cart?.items?.length ?? 0;

    if (itemsCount === 0) {
      toast.error('Please add at least one supplement to continue');
      return;
    }

    doneMutation.mutate();
  };

  if (isLoading) {
    return (
      <MyCartsWrapper title="Subscription Details">
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-8" />
        </div>
      </MyCartsWrapper>
    );
  }

  if (!order && !cart) {
    return (
      <MyCartsWrapper title="Subscription Details">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-slate-500">Subscription context not found.</p>
          </CardContent>
        </Card>
      </MyCartsWrapper>
    );
  }

  return (
    <MyCartsWrapper
      title="Edit Subscription"
      description1="Get a ready-to-use set of products made for you based on your individual test results, carefully selected and divided into individual portions to provide you with a foundational support system for slowing down the aging process or select the supplements you want from our product range. Over time, depending on your test results, your subscription can change on a monthly basis to personalize around you."
    >
      <Card>
        <CardHeader>
          <Heading as="h2" size="h4" className="text-secondary">
            {user?.name} {user?.surname}
          </Heading>
        </CardHeader>
        <CardContent>
          <div className="flex w-full flex-col items-start gap-2.5">
            <UserDataRow caption="Email" value={user?.email ?? '-'} />
            <UserDataRow caption="Phone" value={user?.phone ?? '-'} />
            {user?.birth && (
              <UserDataRow caption="Birth Date" value={user.birth} />
            )}
            {user?.clinic && (
              <UserDataRow caption="Clinic" value={user.clinic.name} />
            )}
          </div>
        </CardContent>
      </Card>

      {currentCartId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <Heading as="h2" size="h4" className="text-secondary">
              Edit Cart Items
            </Heading>
            <Button
              variant="outlinePrimary"
              size="sm"
              onClick={() => setIsPreviewOpen(true)}
            >
              Preview
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6">
              <CartItemsList cartId={currentCartId} />

              {isAddSupplementOpen ? (
                <CartAddItemView
                  cartId={currentCartId}
                  onClose={() => setIsAddSupplementOpen(false)}
                />
              ) : (
                <Button
                  variant="outlinePrimary"
                  animate
                  className="w-fit hover:[&>svg]:rotate-0"
                  onClick={() => setIsAddSupplementOpen(true)}
                >
                  <PlusIcon />
                  <p>Add supplement</p>
                </Button>
              )}
            </div>
          </CardContent>

          <CartPreviewDialog
            open={isPreviewOpen}
            onOpenChange={setIsPreviewOpen}
            items={cart?.items ?? []}
            period={cart?.period}
            priceBreakdown={cart?.price_breakdown}
          />
        </Card>
      )}

      {currentCartId && (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <CartSummary cartId={currentCartId} />

            {isChooseSubscriptionPeriodOpen ? (
              <div className="flex w-full items-center justify-between gap-3 lg:gap-6">
                <Select
                  value={
                    order?.period
                      ? order.period.toString()
                      : cart?.period
                        ? cart.period.toString()
                        : CartPeriod.MONTHLY.toString()
                  }
                  options={cartPeriodOptions}
                  onChange={(value) => handleChangeSubscriptionPeriod(value)}
                />
                <Button
                  onClick={() => setIsChooseSubscriptionPeriodOpen(false)}
                  className="w-1/2"
                >
                  Back
                </Button>
              </div>
            ) : (
              <button
                className="text-primary hover:text-accent active:text-accent flex w-fit cursor-pointer items-center gap-2 font-medium transition-all"
                onClick={() => setIsChooseSubscriptionPeriodOpen(true)}
              >
                <CalendarIcon className="size-6" />
                Choose subscription period
              </button>
            )}

            <Button
              onClick={
                isWalkIn ? handleCreatePaidOrder : handleDoneAddingSupplements
              }
              className="self-center"
              disabled={
                isFetching ||
                doneMutation.isPending ||
                createTempOrder.isPending
              }
            >
              {isWalkIn
                ? createTempOrder.isPending
                  ? 'Creating order…'
                  : 'Create paid order'
                : 'Done adding supplements'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <Heading as="h2" size="h4" className="text-secondary p-0">
            Order History
          </Heading>
        </CardHeader>
        <CardContent className="p-0">
          {userOrders.map((userOrder) => (
            <OrderCard key={userOrder.id} isDoctor={true} order={userOrder} />
          ))}
        </CardContent>
      </Card>
    </MyCartsWrapper>
  );
};
